import Pako from 'pako'
import { isNativeApp } from './Platform'

export const _delay = (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export const _createCanvas = (w, h, bgFill) => {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  if (bgFill) {
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = bgFill
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  return canvas
}

/**
 * @param {CanvasImageSource} image
 * @param {(string | CanvasPattern | CanvasGradient)=} bgFill
 */
export const _imageToCanvas = (image, bgFill) => {
  const canvas = _createCanvas(image.naturalWidth || image.width, image.naturalHeight || image.height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (bgFill) {
    ctx.fillStyle = bgFill
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.drawImage(image, 0, 0)
  return canvas
}

/**
 * @param {CanvasImageSource} image 
 */
export const _imageToDataUri = (image) => {
  return _imageToCanvas(image).toDataURL('image/png')
}

/**
 * @param {CanvasImageSource} image 
 * @param {boolean} vertical 
 */
export const _flipImage = (image, vertical = true) => {
  const canvas = _createCanvas(image.width, image.height)
  const context = canvas.getContext('2d', { willReadFrequently: true })

  // context.save()
  if (vertical) {
    context.scale(1, -1)
    context.drawImage(image, 0, 0, image.width, -image.height)
  } else {
    context.scale(-1, 1)
    context.drawImage(image, 0, 0, -image.width, image.height)
  }
  // context.restore()

  return canvas
}

/**
 * @param {CanvasImageSource} image
 * @param {number} scale
 * @param {number=} height
 */
export const _scaleImage = (image, scale, height) => {
  const w = height ? scale : Math.ceil(image.width * scale)
  const h = height || Math.ceil(image.height * scale)

  const scaled = _createCanvas(w, h)
  const ctx = scaled.getContext('2d', { willReadFrequently: !height }) // backward compatible: no height means willReadFrequently === true
  ctx.drawImage(image, 0, 0, image.width, image.height,
    0, 0, scaled.width, scaled.height)
  return scaled
}

/**
 * Whether an image carries any transparency.
 *
 * Scanned on a downscaled copy: the only question is "does this texture need
 * alpha blending", so a rough sample is enough and the cost stays off the
 * import path. Averaging during the downscale can only pull an alpha value
 * below 255, so a region of transparency is still detected.
 *
 * @param {CanvasImageSource} image
 * @param {number} maxSize longest edge of the copy that gets scanned
 * @returns {boolean}
 */
export const _imageHasAlpha = (image, maxSize = 128) => {
  const w = image.naturalWidth || image.width
  const h = image.naturalHeight || image.height
  if (!w || !h) {
    return false
  }

  try {
    const size = Math.max(w, h)
    const canvas = size > maxSize ? _scaleImage(image, maxSize / size) : _imageToCanvas(image)
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        return true
      }
    }
  } catch (err) {
    // a cross-origin image taints the canvas and getImageData throws. Treat it
    // as opaque, which is how it behaved before this check existed.
    console.warn('[Util] could not read texture alpha', err)
  }

  return false
}

/**
 * @param {ImageData} imgData
 * @param {number} x
 * @param {number} y
 */
export const _getPixel = (imgData, x, y) => {
  const i = (y * imgData.width + x) * 4
  return [
    imgData.data[i],
    imgData.data[i + 1],
    imgData.data[i + 2],
    imgData.data[i + 3],
  ]
}

/**
 * @param {ImageData} imgData
 * @param {number} x
 * @param {number} y
 * @param {number[]} data 
 */
export const _setPixel = (imgData, x, y, data) => {
  const i = (y * imgData.width + x) * 4
  data.forEach((v, vi) => imgData.data[i + vi] = v)
}

/**
 * Convert zlip deflated RGB array to PNG data uri. This is mainly used for
 * PDO importing.
 * @param {Uint8Array} rgbArray 
 * @param {number} width
 * @param {number} height
 * @param {boolean} compressed
 * @param {boolean} flipY
 */
export const _rgbArrayToCanvas = (rgbArray, width, height, compressed = true, flipY = true) => {
  // de-compress data
  if (compressed) {
    // const Pako = await import('pako')
    rgbArray = Pako.inflate(rgbArray)
  }

  const canvas = _createCanvas(width, height)
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const imageData = context.createImageData(width, height)

  let srcIndex = 0, dstIndex = 0, curPixelNum = 0
  for (curPixelNum = 0; curPixelNum < width * height; curPixelNum++) {
    imageData.data[dstIndex] = rgbArray[srcIndex]		// r
    imageData.data[dstIndex + 1] = rgbArray[srcIndex + 1]	// g
    imageData.data[dstIndex + 2] = rgbArray[srcIndex + 2]	// b
    imageData.data[dstIndex + 3] = 255 // 255 = 0xFF - constant alpha, 100% opaque
    srcIndex += 3
    dstIndex += 4
  }
  context.putImageData(imageData, 0, 0)

  // flip image
  if (flipY) {
    return _flipImage(canvas)
  } else {
    return canvas
  }
}

/**
 * @template T
 * @callback ForCallback
 * @param {T} item
 * @param {number} index
 * @returns {void}
 */

/**
 * Allow calling "forEach" on both array and single object.
 * 
 * @template T
 * @param {T | T[]} objs a list of objects or a single variable.
 * @param {ForCallback<T>} callback 
 */
export const _forEach = (objs, callback = () => { }) => {
  if (objs && typeof objs.forEach === 'function') {
    objs.forEach(callback)
  } else {
    callback(objs, 0)
  }
}

/**
 * @template T
 * @callback MapCallback
 * @param {T} item
 * @param {number} index
 * @returns {any}
 */

/**
 * Allow calling "map" on both array and single object.
 * 
 * @template T
 * @param {T | T[]} objs a list of objects or a single variable.
 * @param {MapCallback<T>} callback 
 */
export const _map = (objs, callback = () => { }) => {
  if (objs && typeof objs.map === 'function') {
    return objs.map(callback)
  } else {
    return callback(objs, 0)
  }
}

/**
 * @template T
 * @param {T[]} array
 */
export const _shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 * @param {number} min
 * @param {number} max
 */
export const _getRandomInt = (min, max) => {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Advanced alternative to number.toFixed that does not pad unnecessary zeroes.
 * @param {number} number 
 * @param {number=} fractions maximum number of fraction digits. Default is 2.
 */
export const _toFixed = (number, fractions = 2) => {
  return +parseFloat(number.toFixed(fractions))
}

/**
 * Advanced alternative to number.toFixed that does not pad unnecessary zeroes.
 * @param {number} number 
 * @param {number} fractions maximum number of fraction digits.
 * @param {number} significant maximum number of significant digits.
 */
export const _toFixed2 = (number, fractions, significant = 2) => {
  if (number === 0) {
    return 0
  } else if (Math.abs(number) >= 1) {
    return +parseFloat(number.toFixed(fractions))
  } else {
    let mul = 0, num = number
    while (Math.abs(num) < 1) {
      num *= 10
      mul++
    }
    return _toFixed(number, significant - 1 + mul)
  }
}

export const _toRadians = (degrees) => {
  return degrees * Math.PI / 180
}

export const _toDegrees = (radians) => {
  return radians * 180 / Math.PI
}

/**
 * Get absolute position of an element.
 * @param {HTMLElement} el 
 */
export const _getPos = (el) => {
  let lx, ly
  for (lx = 0, ly = 0; el != null; lx += el.offsetLeft, ly += el.offsetTop, el = el.offsetParent);
  return { x: lx, y: ly }
}

/**
 * @param {any} object
 * @param  {...string} keys
 */
export const _getProp = (object, ...keys) => {
  for (let i = 0; i < keys.length; i++) {
    if (object[keys[i]] !== undefined) {
      return object[keys[i]]
    }
  }
  return undefined
}

/**
 * @param {BufferSource} buffer 
 */
export const _bufferToText = (buffer) => {
  const dec = new TextDecoder('utf-8')
  return dec.decode(buffer)
}

export const _textToBuffer = (text = '') => {
  const enc = new TextEncoder()
  return enc.encode(text)
}

/**
 * Create async function from any function
 * @template T
 * @param {(...params: any[]) => T} f
 * @return {(...params: any[]) => Promise<T>}
 */
export const _makeAsync = (f) => {
  return (...params) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(f(...params))
        } catch (err) {
          reject(err)
        }
      }, 0)
    })
  }
}

export const _disableContextMenu = (el) => {
  const listener = e => e.preventDefault()
  el.addEventListener('contextmenu', listener)
  return listener
}

export const _updateMinMax = (min, max, element, ...props) => {
  props.forEach(p => {
    if (element[p] < min[p]) {
      min[p] = element[p]
    }
    if (element[p] > max[p]) {
      max[p] = element[p]
    }
  })
}

export const _updateMin = (min, element, ...props) => {
  props.forEach(p => {
    if (element[p] < min[p]) {
      min[p] = element[p]
    }
  })
}

export const _updateMax = (max, element, ...props) => {
  props.forEach(p => {
    if (element[p] > max[p]) {
      max[p] = element[p]
    }
  })
}

export const _isTouchDevice = () => {
  // apparently detecting a touch device is impossible
  return true
  // return (('ontouchstart' in window) ||
  //   (navigator.maxTouchPoints > 0) ||
  //   (navigator.msMaxTouchPoints > 0))
}

export const _DEVICE = {
  UNKNOWN: 'unknown',
  WINDOWS_PHONE: 'Windows Phone',
  ANDROID: 'Android',
  IOS: 'iOS',
}

export const _APP = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
}

export const _detectDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return _DEVICE.WINDOWS_PHONE
  }

  if (/android/i.test(userAgent)) {
    return _DEVICE.ANDROID
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return _DEVICE.IOS
  }

  return _DEVICE.UNKNOWN
}

export const _isMobile = () => {
  return _detectDevice() !== _DEVICE.UNKNOWN
}

export const _isWebview = () => {
  // test
  // return true

  // the installed apps (desktop, Android) run in a WebView on purpose: not an in-app browser
  if (isNativeApp()) {
    return false
  }

  const standalone = navigator.standalone
  const userAgent = (navigator.userAgent || navigator.vendor || window.opera).toLowerCase()
  const safari = /safari/.test(userAgent)
  const ios = /iphone|ipod|ipad/.test(userAgent)

  /**
   * @type {{
   *   os: string;
   *   app?: string;
   * } | boolean}
   */
  let isWv = false

  // detect webview of famous apps (Facebook, Instagram)
  if (/instagram/.test(userAgent)) {
    isWv = {
      os: ios ? _DEVICE.IOS : _DEVICE.ANDROID,
      app: _APP.INSTAGRAM,
    }
  } else if (_findAll(userAgent, 'fb').length > 4) {
    isWv = {
      os: ios ? _DEVICE.IOS : _DEVICE.ANDROID,
      app: _APP.FACEBOOK,
    }
  } else if (ios) {
    if (!standalone && safari) {
      // Safari
    } else if (!standalone && !safari) {
      // iOS webview
      isWv = { os: _DEVICE.IOS }
    }
  } else {
    if (userAgent.includes('wv')) {
      // Android webview
      isWv = { os: _DEVICE.ANDROID }
    } else {
      // Chrome
    }
  }

  return isWv
}

/**
 * Wrap a function to give it an interval. The function will not be called
 * more frequent than the interval. Usually this is used to wrap mouse event
 * handler.
 * @template T
 * @param {(...any) => T} f 
 * @param {number} interval 
 * @returns 
 */
export const _throttleCall = (f, interval = 100) => {
  let enableCall = true
  return (...args) => {
    if (!enableCall) return
    enableCall = false
    setTimeout(() => enableCall = true, interval)

    return f(...args)
  }
}

/**
 * Similar to _throttleCall but will ensure the last call is executed.
 * @template T
 * @param {(...any) => T} f
 * @param {number} interval
 * @returns
 */
export const _throttleCallEx = (f, interval = 100) => {
  let enableCall = true
  let lastArgs
  let pending

  const disableCall = () => {
    // disable function execution for a limited time
    enableCall = false
    setTimeout(() => enableCall = true, interval)
  }

  return (...args) => {
    clearTimeout(pending)

    if (!enableCall) {
      // when a call is skipped, save its arguments to call it one last time if no futher call is made
      lastArgs = args
      pending = setTimeout(() => {
        disableCall()

        f(...lastArgs)
      }, interval)
      return
    }

    disableCall()

    // execute function
    return f(...args)
  }
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @return {[ width: number, height: number, ascent: number ]}
 */
export const _computeTextSize = (ctx, text) => {
  const metrics = ctx.measureText(text)
  return [
    metrics.width,
    metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent,
    metrics.actualBoundingBoxAscent,
  ]
}

/**
 * @typedef {{
 *   translate?: number[];
 *   scale?: number[];
 *   rotate?: number[];
 *   matrix?: number[];
 * }} TransformList
 * 
 * @param {SVGGraphicsElement} svg
 */
export const _svgGetTransform = (svg) => {
  const transformList = svg.transform.baseVal
  /** @type {TransformList} */
  const result = {}
  for (let i = 0; i < transformList.numberOfItems; i++) {
    const transform = transformList.getItem(i)
    const m = transform.matrix
    switch (transform.type) {
      case 2:
        result.translate = [m.e, m.f]
        break
      case 3:
        result.scale = [m.a, m.d]
        break
      case 4:
        result.rotate = [transform.angle]
        // TODO need to also handle rotate(angle, x, y) form
        break
      case 5:
        // TODO skewX()
        break
      case 6:
        // TODO skewY(()
        break
      case 1:
      default:
        result.matrix = [m.a, m.b, m.c, m.d, m.e, m.f]
        break
    }
  }
  return result
}

/**
 * @param {string} url
 * @param {((ref?: XMLHttpRequest) => void)=} refCallback
 * @return {Promise<string>}
 */
export const _downloadAsText = (url, refCallback) => {
  const request = new XMLHttpRequest()
  request.responseType = 'text'
  refCallback && refCallback(request)
  return new Promise((resolve, reject) => {
    request.onload = () => {
      resolve(request.responseText)
      refCallback && refCallback()
    }
    request.onerror = (err) => {
      reject(err)
      refCallback && refCallback()
    }
    request.open('GET', url, true)
    request.send(null)
  })
}

/**
 * @param {HTMLImageElement | ImageBitmap} img
 * @param {() => void} onDone
 */
export const _waitImg = (img, onDone) => {
  // Make sure image is finished loading
  if (img.complete || img instanceof ImageBitmap) {
    onDone()
  } else {
    img.addEventListener('load', function () {
      onDone()
    })
  }
}

/**
 * @param {HTMLImageElement} img
 */
export const _waitImgPromise = (img) => {
  return new Promise((resolve) => {
    _waitImg(img, resolve)
  })
}

/**
 * @typedef {import('three').Texture} Texture
 * 
 * @param {Texture} texture
 * @returns {Promise<Texture>}
 */
export const _waitTexture = (texture) => {
  return new Promise(function (resolve, reject) {
    let image = texture.image
    if (image === undefined || image === null) {
      // FIXME: this is not a pleasant logic which should be replaced.
      // THREE.js does not offer a better way (Texture.DEFAULT_IMAGE is
      // undefined pre-r152 and null afterwards, hence the double check).
      Object.defineProperty(texture, 'image', {
        set: function (i) {
          image = i
          resolve()
        },
        get: function () {
          return image
        }
      })
    } else if (image.complete) {
      resolve(texture)
    } else if (typeof image.addEventListener === 'function') {
      image.addEventListener('load', resolve)
      image.addEventListener('error', reject)
    } else {
      resolve()
    }
  })
}

export const _formatNumber = (num = 0, suffixes = ['', 'k', 'm', 'b'], K = 1000) => {
  const M = K * K // 1 million
  const B = M * K // 1 billion
  if (num >= B) {
    return `${_toFixed(num / B, 2)}${suffixes[3]}`
  } else if (num >= M) {
    return `${_toFixed(num / M, 2)}${suffixes[2]}`
  } else if (num >= K) {
    return `${_toFixed(num / K, 1)}${suffixes[1]}`
  } else {
    return num.toString() + suffixes[0]
  }
}

export const _formatFileSize = (size = 0) => {
  return _formatNumber(size, [' Bytes', ' KB', ' MB', ' GB'], 1024)
}

const regEsc = /[-/\\^$*+?.()|[\]{}]/g

export const _findAll = (sentence, sub) => {
  sub = sub.replace(regEsc, '\\$&')
  const regex = new RegExp(sub, 'g')
  let result
  const indices = []
  while ((result = regex.exec(sentence))) {
    indices.push(result.index)
  }
  return indices
}

/**
 * Function composition.
 */
export const _compose = (...fns) => x => fns.reduceRight((y, f) => f(y), x)

/**
 * Deep object comparision that supports object, object with uuid, array, and primitive.
 * @returns {boolean}
 */
export const _compareObject = (obj1, obj2) => {
  if (Array.isArray(obj1)) {
    // array
    return Array.isArray(obj2) && obj1.length === obj2.length && (obj1.length === 0 || // calling reduce on empty array will throws exception
      obj1.reduce((same, e, i) => same && _compareObject(e, obj2[i]), true)
    )
  } else if (obj1 && obj1.uuid) {
    // object with uuid
    return obj1.uuid === (obj2 && obj2.uuid)
  } else if (typeof obj1 === 'object') {
    // general object
    const keys1 = Object.keys(obj1 || {})
    const keys2 = Object.keys(obj2 || {})

    return keys1.length === keys2.length && (keys1.length === 0 || // calling reduce on empty array will throws exception
      keys1.reduce((same, key) => same && key in obj2 && _compareObject(obj1[key], obj2[key]), true)
    )
  } else {
    // primitive
    return obj1 === obj2
  }
}

export const _invalidNumber = (n) => typeof n !== 'number' || isNaN(n) || n === Infinity || n === -Infinity
export const _check3dPoints = (points) => {
  return points.filter(p => _invalidNumber(p.x) || _invalidNumber(p.y) || _invalidNumber(p.z)).map(p => p.toString())
}
export const _check2dPoints = (points) => {
  return points.filter(p => _invalidNumber(p.x) || _invalidNumber(p.y)).map(p => p.toString())
}

/**
 * @template T
 * @callback OnPlotPoint
 * @param {number} x
 * @param {number} y
 * @param {number} index
 * @return {T}
 */

/**
 * @template P
 * @param {number} x0
 * @param {number} y0
 * @param {number} x1
 * @param {number} y1
 * @param {OnPlotPoint<P>} plot 
 */
export const _plotLine = (x0, y0, x1, y1, plot) => {
  const dx = Math.abs(x1 - x0)
  const sx = x0 < x1 ? 1 : -1
  const dy = -Math.abs(y1 - y0)
  const sy = y0 < y1 ? 1 : -1
  let error = dx + dy

  let index = 0
  /** @type {P[]} */
  const res = []
  while (true) {
    res.push(plot(x0, y0, index))
    index++

    if (x0 === x1 && y0 === y1) break
    const e2 = 2 * error
    if (e2 >= dy) {
      if (x0 === x1) break
      error = error + dy
      x0 = x0 + sx
    }
    if (e2 <= dx) {
      if (y0 === y1) break
      error = error + dx
      y0 = y0 + sy
    }
  }

  return res
}

/**
 * Check if data is valid plain data for History.fromPlain (PMK plain text format).
 * @param {any} data
 */
export const _isPlainData = (data) => {
  return typeof data === 'string' && !!data.match(/^\s*\{\n?\s*"version"/)
}

/**
 * Get current site base url with protocol and host name.
 */
export const _getBaseUrl = () => {
  return `${window.location.protocol}//${window.location.host}`
}

export const _getFullUrl = (url) => {
  const script = document.createElement('script')
  script.src = url
  return script.src
}
